import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RunEventRow } from '../lib/fleetStream';
import { buttonClass } from './ui/Button';

// RUN-LEVEL outcome, above the timeline. The header used to say only whether the
// STREAM was live or ended, and `runEventOutcome` (`apps/api`) paints the run's
// terminal `status:run_finished` node green — so a run that died on
// `guard_tripped` rendered "Ended" plus a green Success pill, with the reason
// hidden inside a collapsed <details> among dozens of nodes.
//
// The verdict cannot come from the terminal ping: every one of the 2,854
// `run_finished` payloads in prod is literally `{"event":"run_finished"}`. It
// comes from the run's own `error` events — 4,273 of 7,140 prod runs carry one,
// payload `{message}` in all 4,273 — which is also why this reads the ROWS and
// asks the API for nothing new.

/** The failure a run reported, or null when it reported none. */
export type RunFailure = { readonly message: string | null; readonly seq: number };

/** Anchor id for one event node — shared with `<RunEventNode/>` so a jump can find it. */
export function runEventNodeId(seq: number): string {
  return `run-event-${seq}`;
}

/**
 * The FIRST error the run reported. First, not last: a run that trips a guard
 * then errors on the way out reports the cause first and the consequence after,
 * and the cause is what a reader needs. `null` is not "it succeeded" — it is
 * "no error event", which for a run still in flight is simply not-yet.
 */
export function firstRunFailure(events: readonly RunEventRow[]): RunFailure | null {
  for (const ev of events) {
    if (ev.kind !== 'error') continue;
    let message: string | null = null;
    try {
      const payload: unknown = JSON.parse(ev.payload_json);
      if (typeof payload === 'object' && payload !== null) {
        const raw = (payload as Record<string, unknown>)['message'];
        if (typeof raw === 'string' && raw.trim() !== '') message = raw;
      }
    } catch {
      // A payload that will not parse still proves the run errored; the node
      // below carries the raw text. Never throw out of a render path.
    }
    return { message, seq: ev.seq };
  }
  return null;
}

export type RunOutcomeBannerProps = {
  readonly events: readonly RunEventRow[];
};

export function RunOutcomeBanner(props: RunOutcomeBannerProps): JSX.Element {
  const failure = (): RunFailure | null => firstRunFailure(props.events);
  return (
    <Show when={failure()}>
      {(f) => (
        <div class="alert alert--destructive" role="alert">
          <p class="alert__title">{t('fleet.run_failed')}</p>
          <div class="alert__desc">
            {/* The message is surfaced WITHOUT an expand: it is the answer to
                the question the page was opened with. */}
            <p>{f().message ?? t('fleet.run_failed_desc')}</p>
            <a
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              href={`#${runEventNodeId(f().seq)}`}
            >
              {t('fleet.jump_to_error')}
            </a>
          </div>
        </div>
      )}
    </Show>
  );
}
